import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import ViewContainer from "../../../../../components/layout/view/ViewContainer";
import ViewHeader from "../../../../../components/layout/view/ViewHeader";
import SecondaryPillTabs from "../../../../../components/tabs/SecondaryPillTabs";
import BackButton from "../../../../../components/buttons/back/BackButton";
import EmptyState from "../../../../../components/feedback/EmptyState";
import SearchField from "../../../../../components/input/searchField/SearchField";

import * as groupService from "../../services/memberGroupsService";

import GroupMemberVideosView from "../../../../videos/groups/members/views/GroupMemberVideosView";
import GroupMemberAlbumsView from "../../../../albums/groups/members/views/GroupMemberAlbumsView";
import GroupMemberPopsView from "../../../../pops/groups/members/views/GroupMemberPopsView";
import GroupMemberStreamView from "../../../../streams/groups/members/views/GroupMemberStreamView";
import GroupApprovedMembersView from "../approvedMembers/GroupApprovedMembersView";
import GroupContentSearchView from "../searchContent/GroupContentSearch";

import "./MemberGroupDashboard.css";

const PREVIEW_LIMIT = 5;
const PAGE_LIMIT = 12;

const PREVIEW_TYPES = ["videos", "albums", "pops", "streams"];

const VALID_TABS = new Set([
  "home",
  "videos",
  "albums",
  "pops",
  "streams",
  "members",
]);

const GROUP_TABS = [
  { id: "home", label: "Home" },
  { id: "videos", label: "Videos" },
  { id: "albums", label: "Albums" },
  {
    id: "more",
    label: "More",
    menu: [
      { id: "pops", label: "Pops" },
      { id: "streams", label: "Streams" },
      { id: "members", label: "Members" },
    ],
  },
];

const createPreviewState = (tag) => ({
  tag,
  videos: null,
  albums: null,
  pops: null,
  streams: null,
});

const getErrorMessage = (error) =>
  error?.response?.data?.message || error?.message || "Unable to load group.";

export default function MemberGroupDashboard({ groupTag, onBack }) {
  const { groupTag: routeGroupTag } = useParams();
  const navigate = useNavigate();

  const tag = String(groupTag || routeGroupTag || "").trim();

  const requestRef = useRef(0);

  const [groupState, setGroupState] = useState({
    tag: "",
    data: null,
    loading: true,
    error: "",
  });

  const [activeTab, setActiveTab] = useState("home");
  const [search, setSearch] = useState("");

  const [previews, setPreviews] = useState(() => createPreviewState(tag));

  const isCurrent = groupState.tag === tag;
  const group = isCurrent ? groupState.data : null;
  const loading = !isCurrent || groupState.loading;
  const error = isCurrent ? groupState.error : "";
  const ready = Boolean(group) && !loading;

  const hasSearch = Boolean(search.trim());

  const currentPreviews =
    previews.tag === tag ? previews : createPreviewState(tag);

  const previewsLoaded = PREVIEW_TYPES.every(
    (type) => currentPreviews[type] !== null,
  );

  const hasPreviewContent = PREVIEW_TYPES.some(
    (type) => currentPreviews[type]?.hasContent === true,
  );

  const hasPreviewError = PREVIEW_TYPES.some(
    (type) => currentPreviews[type]?.error === true,
  );

  const showHomeEmpty =
    activeTab === "home" && !hasSearch && previewsLoaded && !hasPreviewContent;

  const previewHandlers = useMemo(() => {
    const handlers = {};

    PREVIEW_TYPES.forEach((type) => {
      handlers[type] = (result) => {
        if (result?.loading) return;

        const next =
          typeof result === "boolean"
            ? {
                hasContent: result,
                error: false,
              }
            : {
                hasContent: Boolean(result?.hasContent),
                error: Boolean(result?.error),
              };

        setPreviews((current) => {
          if (current.tag !== tag) return current;

          const previous = current[type];

          if (
            previous?.hasContent === next.hasContent &&
            previous?.error === next.error
          ) {
            return current;
          }

          return {
            ...current,
            [type]: next,
          };
        });
      };
    });

    return handlers;
  }, [tag]);

  useEffect(() => {
    const requestId = ++requestRef.current;

    setGroupState({
      tag,
      data: null,
      loading: true,
      error: "",
    });

    setActiveTab("home");
    setSearch("");
    setPreviews(createPreviewState(tag));

    if (!tag) {
      setGroupState({
        tag,
        data: null,
        loading: false,
        error: "Group tag is required.",
      });

      return () => {
        requestRef.current += 1;
      };
    }

    const loadGroup = async () => {
      try {
        const response = await groupService.getMemberGroup(tag);
        const data = response?.data;

        if (requestId !== requestRef.current) return;

        if (String(data?.groupTag || "") !== tag) {
          throw new Error("Group details do not match.");
        }

        const status = String(data?.memberStatus ?? data?.status ?? "")
          .trim()
          .toLowerCase();

        const isOwner = data?.isOwner === true || Number(data?.isOwner) === 1;

        if (data.canView === false || (!isOwner && status !== "approved")) {
          throw new Error("Approved group membership required.");
        }

        setGroupState({
          tag,
          data,
          loading: false,
          error: "",
        });

        Promise.resolve()
          .then(() => groupService.updateMemberGroupLastViewed(tag))
          .catch(() => {});
      } catch (requestError) {
        if (requestId !== requestRef.current) return;

        setGroupState({
          tag,
          data: null,
          loading: false,
          error: getErrorMessage(requestError),
        });
      }
    };

    void loadGroup();

    return () => {
      requestRef.current += 1;
    };
  }, [tag]);

  const handleTabChange = (value) => {
    const next =
      typeof value === "string"
        ? value
        : value?.id || value?.value || value?.path;

    if (!VALID_TABS.has(next) || next === activeTab) return;

    setActiveTab(next);
    setSearch("");

    if (next === "home") {
      setPreviews(createPreviewState(tag));
    }
  };

  const handleSearchChange = (event) => {
    setSearch(typeof event === "string" ? event : (event?.target?.value ?? ""));
  };

  const handleSelectContent = (item) => {
    const type = String(item?.contentType || "").toLowerCase();

    if (type === "video") {
      const videoTag = item.contentTag || item.videoTag;

      if (videoTag) {
        navigate(`/watch/${encodeURIComponent(videoTag)}`);
      }

      return;
    }

    const targetTab = {
      album: "albums",
      pop: "pops",
      stream: "streams",
    }[type];

    if (targetTab) {
      handleTabChange(targetTab);
    }
  };

  const handleBack = () => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }

    navigate(-1);
  };

  const renderHome = () => (
    <div className="member-group__content">
      <GroupMemberVideosView
        groupTag={tag}
        preview
        limit={PREVIEW_LIMIT}
        onContentLoaded={previewHandlers.videos}
      />

      <GroupMemberAlbumsView
        groupTag={tag}
        preview
        limit={PREVIEW_LIMIT}
        onContentLoaded={previewHandlers.albums}
      />

      <GroupMemberPopsView
        groupTag={tag}
        preview
        limit={PREVIEW_LIMIT}
        onContentLoaded={previewHandlers.pops}
      />

      <GroupMemberStreamView
        groupTag={tag}
        preview
        limit={PREVIEW_LIMIT}
        onContentLoaded={previewHandlers.streams}
      />

      {showHomeEmpty && (
        <EmptyState
          title={
            hasPreviewError
              ? "Unable to load group content"
              : "No group content available"
          }
          description={
            hasPreviewError
              ? "Some group content could not be retrieved."
              : "Content shared with this group will appear here when available."
          }
        />
      )}
    </div>
  );

  const renderContent = () => {
    if (hasSearch && activeTab !== "members") {
      return (
        <GroupContentSearchView
          groupTag={tag}
          search={search}
          onSelectContent={handleSelectContent}
        />
      );
    }

    switch (activeTab) {
      case "videos":
        return <GroupMemberVideosView groupTag={tag} limit={PAGE_LIMIT} />;

      case "albums":
        return <GroupMemberAlbumsView groupTag={tag} limit={PAGE_LIMIT} />;

      case "pops":
        return <GroupMemberPopsView groupTag={tag} limit={PAGE_LIMIT} />;

      case "streams":
        return <GroupMemberStreamView groupTag={tag} limit={PAGE_LIMIT} />;

      case "members":
        return <GroupApprovedMembersView groupTag={tag} />;

      default:
        return renderHome();
    }
  };

  return (
    <ViewContainer>
      <div className="member-group">
        {loading && (
          <p className="member-group__loading" role="status">
            Loading group...
          </p>
        )}

        {!loading && error && (
          <EmptyState title="Unable to load group" description={error} />
        )}

        {ready && (
          <>
            <div className="member-group__header-top">
              <BackButton label="Groups" onClick={handleBack} />

              {activeTab !== "members" && (
                <div className="member-group__search">
                  <SearchField
                    type="search"
                    value={search}
                    onChange={handleSearchChange}
                    placeholder={`Search ${group.groupName || "group"} content`}
                  />
                </div>
              )}
            </div>

            <ViewHeader
              title={group.groupName || "Group"}
              description={group.groupCode || "Member group"}
            />

            <SecondaryPillTabs
              tabs={GROUP_TABS}
              activeTab={activeTab}
              onChange={handleTabChange}
            />

            <div className="member-group__body">{renderContent()}</div>
          </>
        )}
      </div>
    </ViewContainer>
  );
}
